<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import type { AwardEarned } from '@/api/types';
import { resolveAwardColor } from '@/util/award-color';

const props = defineProps<{
  /** Award to celebrate. Pass `null` to hide. */
  award: AwardEarned | null;
}>();

const emit = defineEmits<{ close: [] }>();

const visible = ref(false);
let dismissTimer: number | undefined;

watch(() => props.award, (a) => {
  if (a) {
    visible.value = true;
    if (dismissTimer) window.clearTimeout(dismissTimer);
    dismissTimer = window.setTimeout(() => dismiss(), 4500);
  } else {
    visible.value = false;
  }
}, { immediate: true });

onMounted(() => { if (props.award) visible.value = true; });
onUnmounted(() => { if (dismissTimer) window.clearTimeout(dismissTimer); });

function dismiss() {
  visible.value = false;
  if (dismissTimer) window.clearTimeout(dismissTimer);
  // wait for fade-out before clearing parent state.
  window.setTimeout(() => emit('close'), 300);
}

const colorHex = computed(() => resolveAwardColor(props.award?.color ?? '#1FA8F2'));
const backdropStyle = computed(() => {
  const base = colorHex.value;
  // Use a centred radial fade so the icon sits on a colour-charged spot.
  return {
    background: `radial-gradient(circle at 50% 45%, ${hexAlpha(base, 0.85)} 0%, ${hexAlpha(base, 0.94)} 40%, ${hexAlpha(darken(base, 0.4), 0.97)} 100%)`,
  };
});

function hexAlpha(hex: string, a: number) {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
function darken(hex: string, factor: number) {
  const { r, g, b } = parseHex(hex);
  const f = 1 - factor;
  const c = (n: number) => Math.max(0, Math.min(255, Math.round(n * f)));
  return `#${[c(r), c(g), c(b)].map(n => n.toString(16).padStart(2, '0')).join('')}`;
}
function parseHex(h: string) {
  const m = h.replace('#', '');
  const n = m.length === 3 ? m.split('').map(c => c + c).join('') : m;
  return {
    r: parseInt(n.slice(0, 2), 16),
    g: parseInt(n.slice(2, 4), 16),
    b: parseInt(n.slice(4, 6), 16),
  };
}

const sparkles = computed(() =>
  Array.from({ length: 14 }, (_, i) => {
    const angle = (i / 14) * 360;
    const radius = 110 + ((i * 17) % 60);
    const delay  = (i * 80) % 800;
    const size   = 14 + ((i * 7) % 16);
    return { angle, radius, delay, size };
  }),
);

const iconName = computed(() => `mdi-${props.award?.icon ?? 'trophy'}`);
</script>

<template>
  <transition name="awd-fade">
    <div v-if="visible && award" class="awd" @click="dismiss">
      <div class="awd__backdrop" :style="backdropStyle" />

      <div class="awd__sparkles">
        <span
          v-for="(s, i) in sparkles"
          :key="i"
          class="awd__sparkle"
          :style="{
            transform: `rotate(${s.angle}deg) translateY(-${s.radius}px)`,
            animationDelay: `${s.delay}ms`,
            fontSize: `${s.size}px`,
          }"
        >★</span>
      </div>

      <div class="awd__stack">
        <div class="awd__icon">
          <v-icon size="96" color="white">{{ iconName }}</v-icon>
        </div>
        <div class="awd__heading">AWARD!</div>
        <div class="awd__name">{{ award.name }}</div>
        <div class="awd__desc">{{ award.description }}</div>
        <div class="awd__hint">tik om door te gaan</div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.awd {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  overflow: hidden;
}

.awd__backdrop {
  position: absolute;
  inset: 0;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.awd-fade-enter-active, .awd-fade-leave-active { transition: opacity 0.32s ease; }
.awd-fade-enter-from, .awd-fade-leave-to       { opacity: 0; }

.awd__sparkles {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.awd__sparkle {
  position: absolute;
  color: #FFD86E;
  text-shadow: 0 0 12px rgba(255, 216, 110, 0.7);
  transform-origin: center;
  animation: awd-sparkle 2.4s ease-out infinite;
  opacity: 0;
}
@keyframes awd-sparkle {
  0%   { opacity: 0; }
  20%  { opacity: 1; }
  60%  { opacity: 1; }
  100% { opacity: 0; }
}

.awd__stack {
  position: relative;
  text-align: center;
  color: white;
  padding: 0 24px;
  animation: awd-pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  max-width: 92vw;
}
@keyframes awd-pop {
  0%   { transform: scale(0.4); opacity: 0; }
  60%  { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); }
}

.awd__icon {
  margin-bottom: 12px;
  animation: awd-float 2.4s ease-in-out infinite;
  filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.35));
}
@keyframes awd-float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
}

.awd__heading {
  font-size: 32px;
  font-weight: 800;
  letter-spacing: 0.18em;
  background: linear-gradient(180deg, #FFE69C 0%, #FFB347 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.3));
  margin-bottom: 8px;
}

.awd__name {
  font-size: 56px;
  font-weight: 900;
  line-height: 1.0;
  text-shadow: 0 6px 30px rgba(0, 0, 0, 0.35), 0 2px 0 rgba(0, 0, 0, 0.18);
  margin: 4px 0 12px;
  word-break: break-word;
}

.awd__desc {
  font-size: 16px;
  font-weight: 500;
  opacity: 0.94;
  margin-bottom: 24px;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

.awd__hint {
  font-size: 11px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  opacity: 0.6;
}
</style>
